/**
 * What a tutor actually sells, in one place.
 *
 * Three components described the plans independently — the Plans tab, the
 * sidebar card and the Overview summary — and they drifted. Overview advertised
 * "from £25.00 a week" by taking the cheaper of the silver and gold tiers, but
 * silver is the tutor's old group-tuition rate, which Bindle Live replaced and
 * which no card on the page offers: the visitor was quoted a price they could
 * not buy at. The facts below are the ones every one of those components reads.
 *
 * The tiers underneath are unchanged. `base` (content library) and `silver`
 * (group tuition) still exist in the database with their prices and their
 * subscriptions; they are simply not sold on their own any more.
 */

/** Bindle Live is not built. Until it is, its card is marked and cannot be bought. */
export const BINDLE_LIVE_ON_SALE = false

/**
 * The intended launch price, not the tutor's silver tier. Group tuition and a
 * scheduled weekly masterclass are different products, so the old rate is not
 * the right number to show against the new one.
 */
export const BINDLE_LIVE_LAUNCH_PRICE = 1.99

export interface PlanAvailabilityInput {
  goldPrice: number
  servicesOffered: string[]
}

/** A tutor offers 1:1 if they say they do and have priced it. */
export function offersOneToOne({ goldPrice, servicesOffered }: PlanAvailabilityInput): boolean {
  return (servicesOffered ?? []).includes('one_on_one') && Number(goldPrice) > 0
}

/**
 * The cheapest plan a visitor can buy today, or null if there is none.
 *
 * Bindle Live is excluded while it is not on sale — quoting £1.99 as the price
 * to start from, when the only thing anyone can actually subscribe to costs
 * £50, would be the same fault in the other direction.
 */
export function lowestAvailablePrice(input: PlanAvailabilityInput): number | null {
  const prices: number[] = []

  if (BINDLE_LIVE_ON_SALE) {
    prices.push(BINDLE_LIVE_LAUNCH_PRICE)
  }

  if (offersOneToOne(input)) {
    prices.push(Number(input.goldPrice))
  }

  return prices.length ? Math.min(...prices) : null
}

/** One line naming what a plan costs, for summaries that have room for a sentence. */
export function planPriceSummary(input: PlanAvailabilityInput): string {
  const lowest = lowestAvailablePrice(input)

  if (lowest !== null) {
    return `1:1 tutoring from £${lowest.toFixed(2)} a week`
  }

  return BINDLE_LIVE_ON_SALE ? 'No plan on sale yet' : 'Bindle Live coming soon'
}
